import { Box, Dialog as MuiDialog, Fade, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type UseModalState } from "@src/lib/ui-react";
import { forwardRef, type ReactNode } from "react";

import { DialogHeader } from "./DialogHeader";
import { useModalsContainerContext } from "./useModalsContainerContext";

/**
 * iOS-style transition configuration for dialogs
 *
 * Dialogs use fade transitions which are faster than slide animations.
 */
const DIALOG_TIMING = {
  enter: 300,
  exit: 250,
};

export interface DialogProps {
  /**
   * The content of the dialog body
   */
  children: React.ReactNode;
  /**
   * Dialog state from useModalState hook
   */
  modalState: UseModalState;
  /**
   * Width of the dialog in pixels
   * @default 400
   */
  width?: number;
  /**
   * Optional title for the dialog header.
   * Ignored when headerContent is provided.
   */
  title?: string;
  /**
   * Show close button in header (only shown if title is provided)
   */
  showCloseButton?: boolean;
  /**
   * Custom header content. When provided, renders instead of DialogHeader.
   * Use this for headers that need more than just a title and close button.
   */
  headerContent?: ReactNode;
  /**
   * Remove default padding from the body content area.
   * When true, children manage their own padding, overflow, and layout.
   * @default false
   */
  disableBodyPadding?: boolean;
  /**
   * Optional footer content (sticky at bottom)
   */
  footerContent?: ReactNode;
  /**
   * Additional styles for the dialog paper
   */
  sx?: SxProps<Theme>;
  /**
   * Custom close handler. Called on backdrop click, Escape key, and X button.
   * Defaults to modalState.closeModal.
   */
  onClose?: () => void;
  /** When false, only an explicitly rendered close control can close the dialog. */
  dismissible?: boolean;
  /**
   * When true, the dialog fills the viewport (no border radius, no margin,
   * full width and height). Useful for tall forms on small screens.
   * @default false
   */
  fullScreen?: boolean;
  /**
   * Visual variant of the dialog.
   * - `modal` (default): centered with a backdrop, scroll lock, and auto-focus.
   * - `floating`: anchored to the bottom-right corner with no backdrop, scroll
   *   lock, or auto-focus; the page behind stays interactive.
   * @default "modal"
   */
  variant?: "modal" | "floating";
}

interface FadeTransitionProps {
  children: React.ReactElement;
  in?: boolean;
}

/**
 * Transition component for the Dialog using Fade
 */
const FadeTransition = forwardRef<HTMLDivElement, FadeTransitionProps>((props, ref) => (
  <Fade
    ref={ref}
    {...props}
    timeout={{
      enter: DIALOG_TIMING.enter,
      exit: DIALOG_TIMING.exit,
    }}
  />
));

FadeTransition.displayName = "FadeTransition";

/**
 * Dialog component for centered dialogs.
 *
 * Features:
 * - Fixed width of 400px (matching Berlin design system)
 * - Optional header with title and close button
 * - Optional sticky footer for actions
 * - iOS-style fade animation
 * - Container scoping support via ModalsContainerProvider
 */
export function Dialog(props: DialogProps) {
  const {
    children,
    modalState,
    width = 400,
    title,
    showCloseButton = true,
    headerContent,
    disableBodyPadding = false,
    footerContent,
    sx,
    onClose,
    dismissible = true,
    fullScreen = false,
    variant = "modal",
  } = props;

  const { containerRef } = useModalsContainerContext();
  const container = containerRef?.current ?? null;

  const hasDialogHeader = !headerContent && Boolean(title);
  const handleClose = onClose ?? modalState.closeModal;
  const isFloating = variant === "floating";

  return (
    <MuiDialog
      open={modalState.modalIsOpen}
      fullScreen={fullScreen}
      TransitionComponent={FadeTransition}
      disableEscapeKeyDown={!dismissible}
      container={container ?? undefined}
      // Floating: no backdrop, page stays interactive, and focus is not trapped.
      hideBackdrop={isFloating}
      disableScrollLock={isFloating}
      disableEnforceFocus={isFloating}
      disableAutoFocus={isFloating}
      sx={mergeSxProps(
        (theme: Theme) => ({
          ...(container && {
            position: "absolute",
            zIndex: theme.zIndex.modal,
          }),
          ...(isFloating && {
            // Let clicks fall through the (backdrop-less) root to the page;
            // the paper re-enables pointer events below.
            pointerEvents: "none",
            zIndex: theme.zIndex.modal,
            "& .MuiDialog-container": {
              justifyContent: "flex-end",
              alignItems: "flex-end",
            },
          }),
          "& .MuiDialog-paper": {
            ...(fullScreen
              ? {
                  width: "100%",
                  maxWidth: "100%",
                  height: "100%",
                  margin: 0,
                  borderRadius: 0,
                  paddingTop: "env(safe-area-inset-top)",
                  paddingBottom: "env(safe-area-inset-bottom)",
                }
              : {
                  width,
                  maxWidth: "calc(100% - 32px)",
                  margin: theme.spacing(4),
                  borderRadius: theme.borderRadius?.xLarge,
                  boxShadow: theme.shadow?.modal,
                }),
            backgroundColor: theme.palette.background?.standout,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            ...(container && {
              position: "absolute",
            }),
            ...(isFloating && {
              pointerEvents: "auto",
            }),
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            ...(container && {
              position: "absolute",
            }),
          },
        }),
        sx
      )}
      onClose={dismissible ? handleClose : undefined}
    >
      {headerContent}
      {hasDialogHeader && (
        <DialogHeader title={title ?? ""} showCloseButton={showCloseButton} onClose={handleClose} />
      )}

      <Box
        sx={(theme) => ({
          flex: 1,
          ...(disableBodyPadding
            ? { minHeight: 0, display: "flex", flexDirection: "column" }
            : { padding: theme.spacing(5), overflow: "auto" }),
        })}
      >
        {children}
      </Box>

      {footerContent && (
        <Box
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
            flexShrink: 0,
          })}
        >
          {footerContent}
        </Box>
      )}
    </MuiDialog>
  );
}
